<!--
# -----------------------------------------------------------------------------
# Component: PolicyCoverage.vue
# Author: PST
# Created Date: 02/April/2019
# Comments: Policy Risk -> Coverage Tab
# -----------------------------------------------------------------------------
-->
<template>
  <div>
    <v-container fluid grid-list-lg>
      <v-layout row wrap>
        <v-flex xs12>
          <v-toolbar dense color="grey lighten-5" flat>
            <v-icon>mdi-format-align-justify</v-icon>
            <v-toolbar-title class="toolbar-title-2">{{ labels.mainTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn class="mx-2" fab dark small color="primary" v-on="on" @click.prevent="showPromptAddItem" :disabled="disableItem">
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Add Risk Item</span>
            </v-tooltip>
            <!-- Added for OS-459 21-Sep-20 -->
            <warning v-model="promptAddItem" acceptLabel="Proceed" title="Select Risk" @ok="doAddItem">
              <v-autocomplete
                :items="riskNos"
                v-model="mainForm.selectedRiskNo"
                :label="labels.selectRiskNo"
                item-text="label"
                item-value="value"
                data-vv-name="mainForm.selectRiskNo"
                :data-vv-as="labels.selectRiskNo"
                v-validate="'required'"
                :error-messages="errors.collect('mainForm.selectRiskNo')"
                clearable
                :loading="polRiskLoading"
              ></v-autocomplete>
            </warning>
          </v-toolbar>
          <v-card-text>
            <v-container fluid grid-list-sm>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-data-table :headers="headers" :items="getItemList" class="elevation-1" :loading="loading">
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.riskItem }}</td>
                        <td>{{ row.item.uom }}</td>
                        <td>{{ row.item.uomShortDesc }}</td>
                        <td>{{ row.item.iu }}</td>
                        <td>{{ row.item.il }}</td>
                        <td>{{ row.item.siLimit }}</td>
                        <td>{{ row.item.uomUn }}</td>
                        <td>{{ row.item.emlPct }}</td>
                        <td>{{ row.item.gap }}</td>
                        <td>{{ row.item.pd }}</td>
                        <td class="table-action-header">
                          <table-action
                            :rowData="row"
                            review
                            @review="doSelectItem(row.item)"
                            :remove="!disableItem && !row.item.delItem"
                            @remove="doPromptRemoveItem(row.item)"
                            :undo="!disableItem && row.item.delItem"
                            undoText="Undo Delete Item"
                            @undo="doPromptUndoDelItem(row.item)"
                          ></table-action>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                  <confirm v-model="promptUndoRemoveItem" @ok="doUndoDelItem">{{ undoDeleteTxt }}</confirm>
                  <confirm v-model="promptRemoveItem" @ok="doRemoveItem">{{ deleteTxt }}</confirm>
                </v-flex>
                <v-flex xs12>
                  <v-card v-show="showExpandRiskItem">
                    <v-toolbar dense color="grey lighten-5" flat>
                      <v-icon>mdi-format-align-justify</v-icon>
                      <v-toolbar-title class="toolbar-title-3">Risk {{ labels.itemTitle }}</v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn class="mx-2" fab dark small color="purple" v-on="on" @click="doItemTextDialog">
                            <v-icon dark>mdi-note-text</v-icon>
                          </v-btn>
                        </template>
                        <span>Item Text</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn class="mx-2" fab dark small color="pink" v-on="on" @click="doPolItemStatsList">
                            <v-icon dark>mdi-chart-bar</v-icon>
                          </v-btn>
                        </template>
                        <span>Item Statistics</span>
                      </v-tooltip>
                      <!-- 2021-01-27 LYT OS-645 -->
                      <v-tooltip top v-if="polMaster.dri === false">
                        <template v-slot:activator="{ on }">
                          <v-btn class="mx-2" fab dark small color="indigo" v-on="on" @click="doPolBordItemList">
                            <v-icon dark>mdi-library-books</v-icon>
                          </v-btn>
                        </template>
                        <span>Bordereaux Item</span>
                      </v-tooltip>
                      <v-btn text icon @click="showExpandRiskItem = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-toolbar>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex xs4>
                            <v-text-field v-model="mainForm.riskGrpNoRiskNo" :label="labels.riskNo" :disabled="true"></v-text-field>
                          </v-flex>
                          <v-flex xs8>
                            <v-text-field v-model="mainForm.busClsDesc" :label="labels.busClsDesc" :disabled="true"></v-text-field>
                          </v-flex>
                          <v-flex xs12>
                            <v-flex xs7>
                              <v-text-field
                                v-model="mainForm.stdRiskTxtRef"
                                :label="labels.stdRiskTxtRef"
                                append-icon="mdi-magnify"
                                @click:append="getHelpList('stdRiskTxtRef')"
                                :disabled="true"
                              ></v-text-field>
                            </v-flex>
                          </v-flex>
                          <v-flex xs4>
                            <v-text-field v-model="mainForm.itemNo" :label="labels.itemNo" :disabled="true"></v-text-field>
                          </v-flex>
                          <v-flex xs8>
                            <v-checkbox :label="labels.delItem" color="primary" v-model="mainForm.delItem" :disabled="true"></v-checkbox>
                          </v-flex>
                          <v-flex xs4>
                            <v-text-field
                              v-model="mainForm.emlPct"
                              :label="labels.emlPct"
                              v-int-mask="{ intScale: 3 }"
                              @blur="getEmlAmt"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs4>
                            <v-text-field v-model="mainForm.emlAmt" :label="labels.emlAmt" :disabled="true"></v-text-field>
                          </v-flex>
                          <!-- 2020-11-19 LYT OS-574 -->
                          <!-- <v-flex xs12 v-if="mainForm.stdRiskTxtRef !== undefined && !this.disableItem"> -->
                          <!-- <v-flex xs12 v-if="mainForm.stdRiskTxtRef !== undefined"> -->
                          <v-flex xs12>
                            <v-flex xs7>
                              <v-text-field
                                readonly
                                v-model="mainForm.stdItemTxtRef"
                                :label="labels.stdItemTxtRef"
                                append-icon="mdi-magnify"
                                @click:append="getHelpList('stdItemTxtRef')"
                                clearable
                                @click:clear="doClearStdItemTxtRef"
                              ></v-text-field>
                            </v-flex>
                          </v-flex>
                          <!-- <v-flex xs12 v-else>
                            <v-text-field v-model="mainForm.stdItemTxtRef" :label="labels.stdItemTxtRef" :disabled="true"></v-text-field>
                          </v-flex> -->
                          <v-flex xs12>
                            <v-textarea v-model="mainForm.stdItemTxtLn" :label="labels.stdItemTxtLn" readonly :rows="5"></v-textarea>
                          </v-flex>
                          <!-- 2021-01-25 LYT OS-646 -->
                          <v-flex lg2 md2 xs12>
                            <v-text-field
                              readonly
                              v-model="mainForm.indGrp"
                              :label="labels.indGrp"
                              :error-messages="errors.collect('indGrp')"
                              data-vv-name="indGrp"
                              :data-vv-as="labels.indGrp"
                              append-icon="mdi-magnify"
                              @click:append="getHelpList('perIndGrp')"
                              :disabled="!perIndGrp"
                            ></v-text-field>
                          </v-flex>
                          <v-flex lg10 md10 xs12>
                            <v-text-field readonly v-model="indGrpDesc" :label="labels.indGrpDesc" :disabled="!perIndGrp"></v-text-field>
                          </v-flex>
                          <v-flex xs12>
                            <v-card>
                              <v-expansion-panels v-model="panel" multiple>
                                <v-expansion-panel>
                                  <v-expansion-panel-header>
                                    <div>
                                      <v-icon>mdi-cash</v-icon><span class="headerTitle">{{ mainForm.riskItem }} {{ labels.uomTitle }}</span>
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-card>
                                      <v-card-text>
                                        <PolicyItemUOM
                                          ref="polItemUOM"
                                          :polItem="mainForm"
                                          :disableItem="disableItem"
                                          :isPkgScheme="isPkgScheme"
                                          @refreshItemDetails="refreshItemDetails"
                                          :hideCRUD="hideTxnIUOMCRUD"
                                          @hidePanel="processShowTxn"
                                        >
                                          ></PolicyItemUOM
                                        >
                                      </v-card-text>
                                    </v-card>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                  <v-expansion-panel-header>
                                    <div>
                                      <v-icon>mdi-window-open</v-icon><span class="headerTitle">{{ labels.discLdgTitle }}</span>
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-card>
                                      <v-card-text>
                                        <PolicyDiscLdg
                                          ref="polDiscLdg"
                                          :polItem="mainForm"
                                          @doSave="doSavePolicyDiscLdg"
                                          :hideCRUD="hideTxnDLCRUD"
                                          @hidePanel="processShowTxn"
                                        >
                                        </PolicyDiscLdg>
                                      </v-card-text>
                                    </v-card>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                  <v-expansion-panel-header>
                                    <div>
                                      <v-icon>mdi-format-list-bulleted</v-icon
                                      ><span class="headerTitle">{{ mainForm.riskItem }} {{ labels.benefitsTitle }}</span>
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-card>
                                      <v-card-text>
                                        <PolicyItemBen
                                          ref="polItemBen"
                                          :polItem="mainForm"
                                          :isPkgScheme="isPkgScheme"
                                          :multiRiskBen="multiRiskBen"
                                          @itemList="riskItemDataAndCalculate"
                                          :hideCRUD="hideTxnPIBCRUD"
                                          @hidePanel="processShowTxn"
                                        ></PolicyItemBen>
                                      </v-card-text>
                                    </v-card>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </v-card>
                          </v-flex>
                          <v-flex xs12>
                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn depressed @click="showExpandRiskItemReset"><span class="btn-normal-error">Close</span></v-btn>
                              <v-btn depressed @click.prevent="doSave"
                                ><span class="btn-normal-primary">{{ labels.save }} Risk Item</span></v-btn
                              >
                            </v-card-actions>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-flex>
                <v-flex xs12>
                  <v-btn class="mr-2" color="error" dark :loading="policy.loading" @click.prevent="doExit">
                    {{ labels.exit }}
                    <v-icon dark right>mdi-exit-to-app</v-icon>
                  </v-btn>
                  <v-btn class="mr-2" color="primary" dark :loading="policy.loading" @click.prevent="doProceed">
                    {{ labels.proceed }}
                    <v-icon dark right>mdi-arrow-right-bold-box</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
        </v-flex>
      </v-layout>
    </v-container>

    <!--Note: Moved all dialog box at the bottom-->
    <v-dialog v-model="itemStatsDialog" width="50%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.itemStats }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="polItemStats.selected"
                    :headers="polItemStats.headers"
                    :items="polItemStats.items"
                    item-key="statsType"
                    class="elevation-1"
                    :loading="polItemStats.loading"
                    :options.sync="polItemStats.pagination"
                  >
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.statsType }}</td>
                        <td>{{ row.item.statsTypeDesc }}</td>
                        <td>{{ row.item.statsVal }}</td>
                        <td>
                          <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                            <v-icon color="info" @click="doShowStatValue(row.item)">mdi-magnify</v-icon>
                          </v-slide-x-reverse-transition>
                        </td>
                        <td>{{ row.item.statsValDesc }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="itemStatsDialog = false"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click="doItemStatsSave"
                    ><span class="btn-normal-primary">{{ labels.save }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statsValDialog" width="60%" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ labels.statsHelpList }}
          <v-spacer></v-spacer>
          <v-btn text icon @click="statsValDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="statsVal.selected"
                    :headers="statsVal.headers"
                    :items="statsVal.items"
                    item-key="statsVal"
                    class="elevation-1"
                    :loading="statsVal.loading"
                  >
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.statsVal }}</td>
                        <td>{{ row.item.statsValDesc }}</td>
                        <td style="text-align: center">
                          <v-btn icon color="primary" text type="submit" @click.prevent="doSelectedStatsValue(row.item)">
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="itemTextDialog" width="50%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.itemText }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12>
                <v-tabs v-model="tabMenu" slider-color="teal">
                  <v-tab ripple :key="1">{{ labels.itemUppTxt }}</v-tab>
                  <v-tab ripple :key="2">{{ labels.itemLwrTxt }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="tabMenu">
                  <v-tab-item :key="1">
                    <v-card>
                      <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12>
                            <v-textarea
                              v-model="itemTextForm.itemUppTxt"
                              :error-messages="errors.collect('itemUppTxt')"
                              data-vv-validate-on="change"
                              data-vv-name="itemUppTxt"
                              :data-vv-as="labels.itemUppTxt"
                              :rows="10"
                            ></v-textarea>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                  <v-tab-item :key="2">
                    <v-card>
                      <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12>
                            <v-textarea
                              v-model="itemTextForm.itemLwrTxt"
                              :error-messages="errors.collect('itemLwrTxt')"
                              data-vv-validate-on="change"
                              data-vv-name="itemLwrTxt"
                              :data-vv-as="labels.itemLwrTxt"
                              :rows="10"
                            ></v-textarea>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="doCloseItemTxt" :loading="policy.loading"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click="doSaveItemTxt" :loading="policy.loading"
                    ><span class="btn-normal-primary">{{ labels.save }}</span></v-btn
                  >
                  <v-btn depressed @click.native="loadPreSetDialog = true" @click="doLoadPreset" :loading="policy.loading"
                    ><span class="btn-normal-primary">{{ labels.preset }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadPreSetDialog" width="70%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.defineText }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex lg6 xs12>
                <v-autocomplete
                  :items="scnRefNoList"
                  v-model="scnRefNo"
                  :label="labels.scnRefNo"
                  item-text="label"
                  item-value="value"
                  data-vv-name="scnRefNo"
                  :data-vv-as="labels.scnRefNo"
                  :error-messages="errors.collect('scnRefNo')"
                  readonly
                ></v-autocomplete>
              </v-flex>
              <v-flex lg6 xs12> </v-flex>
              <v-flex lg6 xs12>
                <v-text-field v-model="busClsDesc" :label="labels.busClsDesc" data-vv-name="busClsDesc" readonly></v-text-field>
              </v-flex>
              <v-flex lg6 xs12> </v-flex>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    :headers="defaultHeaders"
                    :items="defaulText"
                    v-model="defTextSelected"
                    :loading="loadingTxt"
                    item-key="txtRef"
                    :options.sync="pagination"
                    show-select
                    class="elevation-1"
                  >
                  </v-data-table>
                </template>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="loadPreSetDialog = false"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click="doSelectDefText"
                    ><span class="btn-normal-primary">{{ labels.select }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="policy.saving" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-3">
          Processing...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <span class="info-dialog-msg-header">Oops something went wrong...</span>
            <v-spacer class="mt-2"></v-spacer>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="stdRiskTxtHelpList" width="1000px" persistent>
      <StdRiskTxtHelpList
        ref="stdRiskTxtHelpList"
        :busClass="mainForm.busCls"
        @close="doClose"
        @selectStdRiskTxt="doAssignStdRiskTxtHelpList($event)"
      ></StdRiskTxtHelpList>
    </v-dialog>
    <v-dialog v-model="stdItemTxtHelpList" width="1000px" persistent>
      <StdItemTxtHelpList
        ref="stdItemTxtHelpList"
        :riskTxtRef="mainForm.stdRiskTxtRefId"
        :busClass="mainForm.busCls"
        @close="doClose"
        @selectStdItemTxt="doAssignStdItemTxtHelpList($event)"
      ></StdItemTxtHelpList>
    </v-dialog>

    <!-- 2021-01-25 LYT OS-646 -->
    <v-dialog v-model="perIndGrpList" width="900px" persistent>
      <PerIndGrpHelpList
        v-if="perIndGrpHelpList"
        :indGrp="mainForm.indGrp"
        @close="doClose"
        @doAssign="doAssignIndustryHelpList($event)"
      ></PerIndGrpHelpList>
    </v-dialog>

    <!-- 2021-01-27 LYT OS-645 -->
    <v-dialog v-model="showPolBordItemList" width="1280px" persistent>
      <PolicyBordItemList ref="polBordItem" :polItem="mainForm" @close="doClose"></PolicyBordItemList>
    </v-dialog>
  </div>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import PolicyItemUOM from './PolicyItemUOM'
import PolicyDiscLdg from './PolicyDiscLdg'
import PolicyItemBen from './PolicyItemBen'
import StdRiskTxtHelpList from './helplist/StdRiskTxtHelpList'
import StdItemTxtHelpList from './helplist/StdItemTxtHelpList'
import { POLICY_SELECTED_ITEMNO, POLICY_SELECTED_RISKNO, POLICY_RISK_TAB } from '@/store/types'
import { formatPrice } from '@/utils/index'
import { removeFormatNumber } from '@/utils/number'
import * as TableConst from '@/mixins/table'
import PerIndGrpHelpList from './helplist/PerIndGrpHelpList'
import PolicyBordItemList from './PolicyBordItemList'
import clone from 'clone'
import { isEqual } from '@/utils/compare'

export default {
  data() {
    return {
      promptAddItem: false,
      showExpandSumInsured: false,
      showExpandDisc: false,
      showExpandBen: false,
      showExpandRiskItem: true,
      labels: {
        selectRiskNo: 'Risk No.',
        uomTitle: 'Sum Insured / Limits',
        discLdgTitle: 'Discounts / Loadings',
        benefitsTitle: 'Benefits',
        mainTitle: 'Risk Item',
        tableTitle: 'List of Items',
        addItem: 'Add Item',
        delItem: 'Delete Item',
        editItem: 'Edit Item',
        itemTitle: 'Item Detail',
        riskNo: 'Risk No.',
        busCls: 'Business Class',
        stdRiskTxtRef: 'Standard Risk Text',
        itemNo: 'Item No.',
        emlPct: 'EML %',
        emlAmt: 'EML Amount',
        stdItemTxtRef: 'Standard Item Text Reference No.',
        stdItemTxtLn: 'Standard Item Text Line',
        itemText: 'Item Text',
        itemStats: 'Item Statistic',
        itemUppTxt: 'Upper Text',
        itemLwrTxt: 'Lower Text',
        exit: 'Exit',
        reset: 'Reset',
        save: 'Save',
        proceed: 'Proceed',
        preset: 'Load Preset',
        defineText: 'Predefined Text',
        scnRefNo: 'Screen Reference No',
        busClsDesc: 'Business Class',
        select: 'Select',
        statsHelpList: 'List of Statistic Values',
        indGrp: 'Industry Group',
        indGrpDesc: 'Industry Group Description'
      },
      mainForm: {
        riskItem: '',
        selectedRiskNo: undefined,
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        riskGrpNoRiskNo: undefined,
        busCls: undefined,
        busClsDesc: undefined,
        stdRiskTxtRef: undefined,
        stdRiskTxtRefId: undefined,
        emlPct: undefined,
        emlAmt: undefined,
        delItem: undefined,
        stdItemTxtRef: undefined,
        stdItemTxtLn: undefined,
        itemUppTxt: undefined,
        itemLwrTxt: undefined,
        discLdg1Pct: undefined,
        discLdg2Pct: undefined,
        discLdg3Pct: undefined,
        indGrp: undefined
      },
      cloneMainForm: {},
      itemTextForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        itemUppTxt: undefined,
        itemLwrTxt: undefined,
        formType: 'itemTextForm',
        indGrp: undefined
      },
      resetItemTextForm: {
        itemUppTxt: undefined,
        itemLwrTxt: undefined
      },
      loading: false,
      policy: {
        loading: false,
        saving: false
      },
      riskNos: [],
      headers: [
        { text: 'Risk Item', align: 'left', value: 'riskItem' },
        { text: 'UOM', value: 'uom' },
        { text: 'Description', value: 'uomShortDesc' },
        { text: 'I.U.', value: 'iu' },
        { text: 'I.L.', value: 'il' },
        { text: 'SI/Limit', value: 'siLimit' },
        { text: 'UN', value: 'uomUn' },
        { text: 'EML %', value: 'emlPct' },
        { text: 'GAP', value: 'gap' },
        { text: 'PD', value: 'pd' },
        { text: 'Action', value: 'action', align: 'center' }
      ],
      stdRiskTxtHelpList: false,
      stdItemTxtHelpList: false,
      itemList: [],
      isPkgScheme: undefined,
      multiRiskBen: undefined,
      disableItem: true,
      selectedItem: undefined,
      undoDeleteTxt: undefined,
      promptUndoRemoveItem: undefined,
      deleteTxt: undefined,
      promptRemoveItem: undefined,
      itemTextDialog: false,
      tabMenu: undefined,
      loadPreSetDialog: undefined,
      scnRefNoList: [
        {
          label: 'Upper Text',
          value: '30'
        },
        {
          label: 'Lower text',
          value: '31'
        }
      ],
      scnRefNo: undefined,
      busClsDesc: undefined,
      defaultHeaders: [
        { text: 'Text Ref No.', align: 'left', value: 'txtRef' },
        { text: 'Text Title', value: 'txtTitle' },
        { text: 'Default Text', value: 'defTxt' }
      ],
      defaulText: [],
      defTextSelected: [],
      loadingTxt: undefined,
      pagination: undefined,
      selectedItemUppTxt: [],
      selectedItemLwrTxt: [],
      itemsItemUppTxt: [],
      itemsItemLwrTxt: [],
      polItemStats: {
        selected: [],
        headers: [
          { text: 'Statistics Type', value: 'statsType' },
          { text: 'Description', value: 'statsTypeDesc' },
          { text: 'Statistics Value', value: 'statsVal' },
          { text: '', value: 'searchStatsValue' },
          { text: 'Description', value: 'statsValDesc' }
        ],
        items: [],
        pagination: {
          itemsPerPage: -1
        },
        loading: undefined,
        saving: undefined
      },
      statsVal: {
        selected: [],
        headers: [
          { text: 'Statistics Value', value: 'statsVal' },
          { text: 'Description', value: 'statsValDesc' },
          { text: 'Action', align: 'center', sortable: false, value: '' }
        ],
        items: [],
        loading: undefined
      },
      statsValDialog: false,
      polItemStatsForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        statsType: [],
        statsVal: []
      },
      itemStatsDialog: false,
      showErrorDialog: false,
      discLdgCodeList: [],
      modeSave: 'Save',
      modeProceed: 'Proceed',
      policyItemForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined
      },
      polRiskLoading: false,
      perIndGrp: false,
      perIndGrpHelpList: false,
      perIndGrpList: false,
      indGrpDesc: undefined,
      industryList: [],
      showPolBordItemList: false,
      panel: [0, 1, 2],
      progressTxnPIB: false,
      progressTxnDL: false,
      progressTxnIUOM: false,
      saveWhenLeave: false,
      coverageSelectedRiskGrpNo: 1,
      coverageSelectedRiskNo: 1,
      coverageSelectedItemNo: 1
    }
  },
  watch: {
    'mainForm.itemNo': function(value) {
      this.$store.commit(POLICY_SELECTED_ITEMNO, value)
    },
    'mainForm.riskNo': function(value) {
      this.$store.commit(POLICY_SELECTED_RISKNO, value)
    },
    displayErrorDialog: {
      handler: function(messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    },
    discLdgCodeList: function(listValue) {
      this.$refs.polDiscLdg.setDiscLdgList(listValue)
    }
  },
  computed: {
    polMaster() {
      return this.$store.state.underwriting.policy.polMaster
    },
    selectedRiskGrpNo() {
      return this.$store.state.underwriting.policy.selectedRiskGrpNo
    },
    selectedRiskNo() {
      return this.$store.state.underwriting.policy.selectedRiskNo
    },
    selectedItemNo() {
      return this.$store.state.underwriting.policy.selectedItemNo
    },
    getFirstItemList() {
      return this.itemList.filter(item => {
        return item.riskItem === this.itemList[0].riskItem
      })
    },
    getNewItem() {
      return this.itemList.filter(item => {
        return item.newItemNo === true
      })
    },
    displayErrorDialog() {
      return this.messages
    },
    getIndGrpDesc() {
      return this.industryList.filter(indGrp => {
        return indGrp.value === this.mainForm.indGrp
      })
    },
    checkItemListByRiskNo() {
      let iCnt = 0
      this.itemList.filter(item => {
        if (parseInt(item.riskNo) > 1) {
          iCnt++
        }
      })
      return iCnt
    },
    checkItemListByItemNo() {
      let iCnt = 0
      this.itemList.filter(item => {
        if (item.riskNo === this.mainForm.riskNo && parseInt(item.itemNo) > 1) {
          iCnt++
        }
      })
      return iCnt
    },
    getItemList() {
      return this.itemList
    },
    hideTxnPIBCRUD: function() {
      return this.progressTxnPIB
    },
    hideTxnDLCRUD: function() {
      return this.progressTxnDL
    },
    hideTxnIUOMCRUD: function() {
      return this.progressTxnIUOM
    }
  },
  methods: {
    async doInit() {
      this.processShowTxn('RESET')
      this.getRiskItemParams() // 2021-01-26 LYT OS-646
      this.getRiskList() // 2020-12-17 LYT SR5681
      this.getRiskItemList().then(() => {
        if (this.getFirstItemList.length > 0) {
          if (this.mainForm.riskGrpNoRiskNo === undefined) {
            this.doSelectItem(this.getFirstItemList[0])
          } else {
            if (this.$refs.polItemBen) this.$refs.polItemBen.doInit()
          }
          this.panel = [0, 2]
        }
      })
    },
    async getRiskItemParams() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.riskItemParams
      if (apiUrl) {
        try {
          const expand = {
            scheme: this.polMaster.scheme
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data.isPkgScheme) {
            this.isPkgScheme = result.data.isPkgScheme
            this.disableItem = this.isPkgScheme
          } else {
            this.disableItem = false
          }
          this.multiRiskBen = result.data.multiRiskBen
          if (result.data.discLdgList) {
            this.discLdgCodeList = result.data.discLdgList
          }
          if (result.data.industryList) {
            this.$set(this, 'industryList', result.data.industryList)
          }
        } catch (err) {
          this.$toast.error('An unexpected error occurred. Please try again, or contact the system administrator if the error persists.')
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doUndoDelItem() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.undoDelItem
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.loading = true
          this.policyItemForm.polNo = this.polMaster.polNo
          this.policyItemForm.renCnt = this.polMaster.renCnt
          this.policyItemForm.endtCnt = this.polMaster.endtCnt
          this.policyItemForm.riskGrpNo = this.selectedItem.riskGrpNo
          this.policyItemForm.riskNo = this.selectedItem.riskNo
          this.policyItemForm.itemNo = this.selectedItem.itemNo
          let result = await this.$http.post(apiUrl, this.policyItemForm)
          if (result) {
            this.$toast.success('Item successfully updated.')
            this.mainForm.riskGrpNoRiskNo = undefined
            this.doInit()
          } else {
            this.$toast.error('An error encountered unable to undo delete item.')
          }
          this.loading = false
          this.policy.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
          this.policy.loading = false
        }
      } else {
        this.$toast.error('An error encountered unable to undo delete item.')
      }
    },
    getHelpList(fieldName) {
      this.resetMessages()
      if (fieldName === 'stdRiskTxtRef') {
        this.stdRiskTxtHelpList = true
        this.stdItemTxtHelpList = false
        if (this.$refs.stdRiskTxtHelpList) this.$refs.stdRiskTxtHelpList.doInit()
      } else if (fieldName === 'perIndGrp') {
        this.perIndGrpHelpList = true
        this.perIndGrpList = true
      } else {
        this.stdRiskTxtHelpList = false
        this.stdItemTxtHelpList = true
        if (this.$refs.stdItemTxtHelpList) this.$refs.stdItemTxtHelpList.doInit()
      }
    },
    doClose() {
      this.stdRiskTxtHelpList = false
      this.stdItemTxtHelpList = false
      this.perIndGrpHelpList = false
      this.perIndGrpList = false
      this.showPolBordItemList = false
    },
    doAssignStdRiskTxtHelpList(stdRiskTxt) {
      this.mainForm.stdRiskTxtRef = stdRiskTxt.txtTitle
      this.mainForm.stdRiskTxtRefId = stdRiskTxt.txtRef
    },
    doAssignStdItemTxtHelpList(stdItemTxt) {
      this.mainForm.stdItemTxtRef = stdItemTxt.txtRef
      this.mainForm.stdItemTxtLn = stdItemTxt.txtTitleDesc
    },
    async getRiskItemList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.riskItemList
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.saving = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.coverageSelectedRiskGrpNo,
            riskNo: this.coverageSelectedRiskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            this.itemList = []
            data.data.map(item => {
              const convertItem = {
                riskItem:
                  item.riskGrpNo.toString().padStart(2, '0') +
                  '/' +
                  item.riskNo.toString().padStart(3, '0') +
                  '/' +
                  item.itemNo.toString().padStart(3, '0'),
                riskGrpNo: item.riskGrpNo.toString().padStart(2, '0'),
                riskNo: item.riskNo.toString().padStart(3, '0'),
                itemNo: item.itemNo.toString().padStart(3, '0'),
                uom: item.uom,
                uomShortDesc: item.uomShortDesc,
                iu: item.iu,
                il: item.il,
                siLimit: formatPrice(item.siLimit),
                uomUn: item.uomUn,
                emlPct: item.emlPct,
                gap: formatPrice(item.gap),
                pd: formatPrice(item.pd),
                busCls: item.busCls,
                delItem: item.delItem,
                busClsDesc: item.busClsDesc,
                stdRiskTxtRef: item.stdRiskTxtRef,
                stdRiskTxtLn: item.stdRiskTxtLn,
                emlAmt: item.emlAmt.toFixed(2),
                stdItemTxtRef: item.stdItemTxtRef,
                stdItemTxtLn: item.stdItemTxtLn,
                newItemNo: item.newItemNo,
                itemUppTxt: item.itemUppTxt,
                itemLwrTxt: item.itemLwrTxt,
                discLdg1Pct: item.discLdg1Pct,
                discLdg2Pct: item.discLdg2Pct,
                discLdg3Pct: item.discLdg3Pct,
                perIndGrp: item.perIndGrp,
                indGrp: item.indGrp
              }
              // Commented for OS-459 21-Sep-2020
              // 2020-12-17 LYT SR5681
              // const tmpRiskNos = {
              //   label: 'Risk ' + item.riskGrpNo.toString().padStart(2, '0') + '/' + item.riskNo.toString().padStart(3, '0'),
              //   value: item.riskGrpNo.toString().padStart(2, '0') + '/' + item.riskNo.toString().padStart(3, '0')
              // }
              // this.riskNos.push(tmpRiskNos)
              this.itemList.push(convertItem)
            })
            this.loading = false
            this.policy.saving = false
            this.calcAndUpdatePRAndPM()
          } else {
            this.itemList = []
            this.loading = false
            this.policy.saving = false
          }
        } catch (err) {
          this.loading = false
          this.policy.saving = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async riskItemData() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.riskItemData
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.saving = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.coverageSelectedRiskGrpNo,
            riskNo: this.coverageSelectedRiskNo,
            itemNo: this.coverageSelectedItemNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            let item = data
            const convertItem = {
              riskItem:
                item.riskGrpNo.toString().padStart(2, '0') +
                '/' +
                item.riskNo.toString().padStart(3, '0') +
                '/' +
                item.itemNo.toString().padStart(3, '0'),
              riskGrpNo: item.riskGrpNo.toString().padStart(2, '0'),
              riskNo: item.riskNo.toString().padStart(3, '0'),
              itemNo: item.itemNo.toString().padStart(3, '0'),
              uom: item.uom,
              uomShortDesc: item.uomShortDesc,
              iu: item.iu,
              il: item.il,
              siLimit: formatPrice(item.siLimit),
              uomUn: item.uomUn,
              emlPct: item.emlPct,
              gap: formatPrice(item.gap),
              pd: formatPrice(item.pd),
              busCls: item.busCls,
              delItem: item.delItem,
              busClsDesc: item.busClsDesc,
              stdRiskTxtRef: item.stdRiskTxtRef,
              stdRiskTxtLn: item.stdRiskTxtLn,
              emlAmt: item.emlAmt.toFixed(2),
              stdItemTxtRef: item.stdItemTxtRef,
              stdItemTxtLn: item.stdItemTxtLn,
              newItemNo: item.newItemNo,
              itemUppTxt: item.itemUppTxt,
              itemLwrTxt: item.itemLwrTxt,
              discLdg1Pct: item.discLdg1Pct,
              discLdg2Pct: item.discLdg2Pct,
              discLdg3Pct: item.discLdg3Pct,
              perIndGrp: item.perIndGrp,
              indGrp: item.indGrp
            }
            const index = this.itemList.findIndex(item => item.riskItem === convertItem.riskItem)
            if (index >= 0) {
              let tempItemList = clone(this.itemList)
              tempItemList[index] = convertItem
              this.itemList = clone(tempItemList)
            }
          }
        } catch (err) {
          this.loading = false
          this.policy.saving = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
      this.loading = false
      this.policy.saving = false
    },
    calcAndUpdatePRAndPM() {
      const expand = {
        polNo: this.polMaster.polNo,
        renCnt: this.polMaster.renCnt,
        endtCnt: this.polMaster.endtCnt,
        riskGrpNo: this.coverageSelectedRiskGrpNo
      }
      this.$store.dispatch('calcAndUpdatePRAndPM', expand)
    },
    calcAndUpdateSpecificPRAndPM() {
      const expand = {
        polNo: this.polMaster.polNo,
        renCnt: this.polMaster.renCnt,
        endtCnt: this.polMaster.endtCnt,
        riskGrpNo: this.coverageSelectedRiskGrpNo,
        riskNo: this.coverageSelectedRiskNo
      }
      this.$store.dispatch('calcAndUpdateSpecificPRAndPM', expand)
    },
    riskItemDataAndCalculate() {
      this.riskItemData()
      this.calcAndUpdateSpecificPRAndPM()
    },
    riskItemDataAndCalculateAndRiskItemList() {
      this.riskItemData()
      this.calcAndUpdateSpecificPRAndPM()
      this.getRiskItemBenList()
    },
    async doSelectItem(data) {
      this.showExpandRiskItem = true
      this.mainForm.riskItem = data.riskItem
      this.mainForm.riskGrpNoRiskNo = data.riskGrpNo.toString().padStart(2, '0') + '/' + data.riskNo.toString().padStart(3, '0')
      this.mainForm.busCls = data.busCls
      this.mainForm.busClsDesc = data.busCls + ' - ' + data.busClsDesc
      if (data.stdRiskTxtRef) {
        this.mainForm.stdRiskTxtRefId = data.stdRiskTxtRef
        this.mainForm.stdRiskTxtRef = data.stdRiskTxtRef + ' - ' + data.stdRiskTxtLn
      } else {
        this.mainForm.stdRiskTxtRefId = undefined
        this.mainForm.stdRiskTxtRef = undefined
      }
      this.mainForm.riskGrpNo = data.riskGrpNo
      this.mainForm.riskNo = data.riskNo
      this.mainForm.itemNo = data.itemNo
      this.mainForm.emlPct = data.emlPct
      this.mainForm.emlAmt = data.emlAmt
      this.mainForm.delItem = data.delItem
      this.mainForm.stdItemTxtRef = data.stdItemTxtRef
      this.mainForm.stdItemTxtLn = data.stdItemTxtLn
      this.mainForm.discLdg1Pct = data.discLdg1Pct
      this.mainForm.discLdg2Pct = data.discLdg2Pct
      this.mainForm.discLdg3Pct = data.discLdg3Pct
      this.mainForm.indGrp = data.indGrp // 2021-01-26 LYT OS-646
      this.busClsDesc = data.busClsDesc
      this.perIndGrp = data.perIndGrp // 2021-01-26 LYT OS-646
      this.itemTextForm.itemUppTxt = data.itemUppTxt
      this.itemTextForm.itemLwrTxt = data.itemLwrTxt
      this.resetItemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt
      this.resetItemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt
      this.cloneMainForm = clone(this.mainForm)
      this.getItemTextList()
      this.doIndGrpDesc() // 2021-01-26 LYT OS-646
      if (this.$refs) {
        if (this.$refs.polItemUOM) this.$refs.polItemUOM.doInit()
        if (this.$refs.polDiscLdg) {
          this.$refs.polDiscLdg.initMainForm()
          this.getRiskItemParams()
        }
        if (this.$refs.polItemBen) this.$refs.polItemBen.doInit()
      }
      this.coverageSelectedRiskGrpNo = data.riskGrpNo
      this.coverageSelectedRiskNo = data.riskNo
      this.coverageSelectedItemNo = data.itemNo
    },
    doPromptRemoveItem(data) {
      this.selectedItem = data
      this.deleteTxt = 'Confirm to delete Risk Item ' + data.riskItem + '?'
      this.promptRemoveItem = true
    },
    doPromptUndoDelItem(data) {
      this.selectedItem = data
      this.undoDeleteTxt = 'Confirm to undo delete Risk Item ' + data.riskItem + '?'
      this.promptUndoRemoveItem = true
    },
    doAddItem() {
      this.resetMessages()
      this.policy.saving = true
      this.$validator.validateAll().then(result => {
        if (result) {
          this.addItem().then(() => {
            if (this.getNewItem.length > 0) {
              this.showExpandRiskItem = true
              this.mainForm.riskItem = this.getNewItem[0].riskItem
              this.mainForm.riskGrpNoRiskNo =
                this.getNewItem[0].riskGrpNo.toString().padStart(2, '0') + '/' + this.getNewItem[0].riskNo.toString().padStart(3, '0')
              this.mainForm.busCls = this.getNewItem[0].busCls
              this.mainForm.busClsDesc = this.getNewItem[0].busCls + ' - ' + this.getNewItem[0].busClsDesc
              if (this.getNewItem[0].stdRiskTxtRef) {
                this.mainForm.stdRiskTxtRefId = this.getNewItem[0].stdRiskTxtRef
                this.mainForm.stdRiskTxtRef = this.getNewItem[0].stdRiskTxtRef + ' - ' + this.getNewItem[0].stdRiskTxtLn
              } else {
                this.mainForm.stdRiskTxtRefId = undefined
                this.mainForm.stdRiskTxtRef = undefined
              }
              this.mainForm.riskGrpNo = this.getNewItem[0].riskGrpNo
              this.mainForm.riskNo = this.getNewItem[0].riskNo
              this.mainForm.itemNo = this.getNewItem[0].itemNo
              this.mainForm.emlPct = this.getNewItem[0].emlPct
              this.mainForm.emlAmt = this.getNewItem[0].emlAmt
              this.mainForm.delItem = this.getNewItem[0].delItem
              this.mainForm.stdItemTxtRef = this.getNewItem[0].stdItemTxtRef
              this.mainForm.stdItemTxtLn = this.getNewItem[0].stdItemTxtLn
              this.mainForm.discLdg1Pct = this.getNewItem[0].discLdg1Pct
              this.mainForm.discLdg2Pct = this.getNewItem[0].discLdg2Pct
              this.mainForm.discLdg3Pct = this.getNewItem[0].discLdg3Pct
              this.mainForm.indGrp = this.getNewItem[0].indGrp // 2021-01-26 LYT OS-646
              this.busClsDesc = this.getNewItem[0].busClsDesc
              this.perIndGrp = this.getNewItem[0].perIndGrp // 2021-01-26 LYT OS-646
              this.itemTextForm.itemUppTxt = this.getNewItem[0].itemUppTxt
              this.itemTextForm.itemLwrTxt = this.getNewItem[0].itemLwrTxt
              this.resetItemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt
              this.resetItemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt
              this.cloneMainForm = clone(this.mainForm)
              this.getItemTextList()
              this.doIndGrpDesc() // 2021-01-26 LYT OS-646
              this.calcAndUpdateSpecificPRAndPM()
              if (this.$refs) {
                if (this.$refs.polItemUOM) this.$refs.polItemUOM.doInit()
                if (this.$refs.polDiscLdg) {
                  this.$refs.polDiscLdg.initMainForm()
                  this.getRiskItemParams()
                }
                if (this.$refs.polItemBen) this.$refs.polItemBen.doInit()
              }
            }
          })
        } else {
          this.promptAddItem = true
        }
      })
      this.policy.saving = false
    },
    showPromptAddItem() {
      this.resetMessages()
      this.promptAddItem = true
      this.mainForm.selectedRiskNo = ''
    },
    async addItem() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.addItem
      if (apiUrl) {
        try {
          // Commented for OS-459 21-Sep-2020
          // this.mainForm.riskGrpNoRiskNo = this.getNewItem[0].riskGrpNo.toString().padStart(2, '0') + '/' + this.getNewItem[0].riskNo.toString().padStart(3, '0')
          this.mainForm.riskGrpNoRiskNo = this.mainForm.selectedRiskNo
          let riskNoArr = this.mainForm.selectedRiskNo ? this.mainForm.selectedRiskNo.split('/') : []
          this.loading = true
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            // riskGrpNo: this.selectedRiskGrpNo,
            // riskNo: this.selectedRiskNo
            riskGrpNo: riskNoArr[0],
            riskNo: riskNoArr[1]
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            let item = data
            const convertItem = {
              riskItem:
                item.riskGrpNo.toString().padStart(2, '0') +
                '/' +
                item.riskNo.toString().padStart(3, '0') +
                '/' +
                item.itemNo.toString().padStart(3, '0'),
              riskGrpNo: item.riskGrpNo.toString().padStart(2, '0'),
              riskNo: item.riskNo.toString().padStart(3, '0'),
              itemNo: item.itemNo.toString().padStart(3, '0'),
              uom: item.uom,
              uomShortDesc: item.uomShortDesc,
              iu: item.iu,
              il: item.il,
              siLimit: item.siLimit.toFixed(2),
              uomUn: item.uomUn,
              emlPct: item.emlPct,
              gap: formatPrice(item.gap),
              pd: formatPrice(item.pd),
              busCls: item.busCls,
              delItem: item.delItem,
              busClsDesc: item.busClsDesc,
              stdRiskTxtRef: item.stdRiskTxtRef,
              stdRiskTxtLn: item.stdRiskTxtLn,
              emlAmt: item.emlAmt.toFixed(2),
              stdItemTxtRef: item.stdItemTxtRef,
              stdItemTxtLn: item.stdItemTxtLn,
              newItemNo: item.newItemNo,
              itemUppTxt: item.itemUppTxt,
              itemLwrTxt: item.itemLwrTxt,
              discLdg1Pct: item.discLdg1Pct,
              discLdg2Pct: item.discLdg2Pct,
              discLdg3Pct: item.discLdg3Pct,
              perIndGrp: item.perIndGrp,
              indGrp: item.indGrp
            }
            this.itemList.push(convertItem)
            this.coverageSelectedRiskGrpNo = item.riskGrpNo.toString().padStart(2, '0')
            this.coverageSelectedRiskNo = item.riskNo.toString().padStart(3, '0')
            this.coverageSelectedItemNo = item.itemNo.toString().padStart(3, '0')
            this.$toast.success('New Item Added.')
          }
          this.loading = false
          this.policy.loading = false
        } catch (err) {
          this.loading = false
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doItemTextDialog() {
      this.itemTextDialog = true
    },
    async doPolItemStatsList() {
      this.itemStatsDialog = true
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.itemStatsList
      if (apiUrl) {
        try {
          this.polItemStats.items = []
          this.polItemStats.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            itemNo: this.mainForm.itemNo,
            busCls: this.mainForm.busCls
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(statistic => {
              const statisticList = {
                statsType: statistic.statsType,
                statsTypeDesc: statistic.statsTypeDesc,
                statsVal: statistic.statsValue,
                statsValDesc: statistic.statsValueDesc
              }
              this.polItemStats.items.push(statisticList)
            })
            this.polItemStats.loading = false
          } else {
            this.polItemStats.items = []
            this.polItemStats.loading = false
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doRemoveItem() {
      this.delItem().then(() => {
        if (this.getFirstItemList.length > 0) {
          this.mainForm.riskGrpNoRiskNo =
            this.getFirstItemList[0].riskGrpNo.toString().padStart(2, '0') + '/' + this.getFirstItemList[0].riskNo.toString().padStart(3, '0')
          this.mainForm.busCls = this.getFirstItemList[0].busCls
          this.mainForm.busClsDesc = this.getFirstItemList[0].busCls + ' - ' + this.getFirstItemList[0].busClsDesc
          if (this.getFirstItemList[0].stdRiskTxtRef) {
            this.mainForm.stdRiskTxtRefId = this.getFirstItemList[0].stdRiskTxtRef
            this.mainForm.stdRiskTxtRef = this.getFirstItemList[0].stdRiskTxtRef + ' - ' + this.getFirstItemList[0].stdRiskTxtLn
          } else {
            this.mainForm.stdRiskTxtRefId = undefined
            this.mainForm.stdRiskTxtRef = undefined
          }
          this.mainForm.riskGrpNo = this.getFirstItemList[0].riskGrpNo
          this.mainForm.riskNo = this.getFirstItemList[0].riskNo
          this.mainForm.itemNo = this.getFirstItemList[0].itemNo
          this.mainForm.emlPct = this.getFirstItemList[0].emlPct
          this.mainForm.emlAmt = this.getFirstItemList[0].emlAmt
          this.mainForm.delItem = this.getFirstItemList[0].delItem
          this.mainForm.stdItemTxtRef = this.getFirstItemList[0].stdItemTxtRef
          this.mainForm.stdItemTxtLn = this.getFirstItemList[0].stdItemTxtLn
          this.mainForm.discLdg1Pct = this.getFirstItemList[0].discLdg1Pct
          this.mainForm.discLdg2Pct = this.getFirstItemList[0].discLdg2Pct
          this.mainForm.discLdg3Pct = this.getFirstItemList[0].discLdg3Pct
          this.mainForm.indGrp = this.getFirstItemList[0].indGrp // 2021-01-26 LYT OS-646
          this.busClsDesc = this.getFirstItemList[0].busClsDesc
          this.perIndGrp = this.getFirstItemList[0].perIndGrp // 2021-01-26 LYT OS-646
          this.itemTextForm.itemUppTxt = this.getFirstItemList[0].itemUppTxt
          this.itemTextForm.itemLwrTxt = this.getFirstItemList[0].itemLwrTxt
          this.resetItemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt
          this.resetItemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt
          this.cloneMainForm = clone(this.mainForm)
          this.getItemTextList()
          this.doIndGrpDesc() // 2021-01-26 LYT OS-646
          if (this.$refs) {
            if (this.$refs.polItemUOM) this.$refs.polItemUOM.doInit()
            if (this.$refs.polDiscLdg) this.$refs.polDiscLdg.initMainForm()
            if (this.$refs.polItemBen) this.$refs.polItemBen.doInit()
          }
          this.calcAndUpdateSpecificPRAndPM()
          this.getRiskItemList() // OS-1179
        } else {
          this.doInit()
        }
      })
    },
    async delItem() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.delItem
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.selectedItem.riskGrpNo,
            riskNo: this.selectedItem.riskNo,
            itemNo: this.selectedItem.itemNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            let item = data.data
            if (item) {
              const convertItem = {
                riskItem:
                  item.riskGrpNo.toString().padStart(2, '0') +
                  '/' +
                  item.riskNo.toString().padStart(3, '0') +
                  '/' +
                  item.itemNo.toString().padStart(3, '0'),
                riskGrpNo: item.riskGrpNo.toString().padStart(2, '0'),
                riskNo: item.riskNo.toString().padStart(3, '0'),
                itemNo: item.itemNo.toString().padStart(3, '0'),
                uom: item.uom,
                uomShortDesc: item.uomShortDesc,
                iu: item.iu,
                il: item.il,
                siLimit: item.siLimit.toFixed(2),
                uomUn: item.uomUn,
                emlPct: item.emlPct,
                delItem: item.delItem,
                gap: formatPrice(item.gap),
                pd: formatPrice(item.pd),
                busCls: item.busCls,
                busClsDesc: item.busClsDesc,
                stdRiskTxtRef: item.stdRiskTxtRef,
                stdRiskTxtLn: item.stdRiskTxtLn,
                emlAmt: item.emlAmt.toFixed(2),
                stdItemTxtRef: item.stdItemTxtRef,
                stdItemTxtLn: item.stdItemTxtLn,
                newItemNo: item.newItemNo,
                itemUppTxt: item.itemUppTxt,
                itemLwrTxt: item.itemLwrTxt,
                discLdg1Pct: item.discLdg1Pct,
                discLdg2Pct: item.discLdg2Pct,
                discLdg3Pct: item.discLdg3Pct,
                perIndGrp: item.perIndGrp,
                indGrp: item.indGrp
              }
              const index = this.itemList.findIndex(item => item.riskItem === convertItem.riskItem)
              if (index >= 0) {
                let tempItemList = clone(this.itemList)
                tempItemList[index] = convertItem
                this.itemList = clone(tempItemList)
              }
            } else {
              let riskItem =
                this.selectedItem.riskGrpNo.toString().padStart(2, '0') +
                '/' +
                this.selectedItem.riskNo.toString().padStart(3, '0') +
                '/' +
                this.selectedItem.itemNo.toString().padStart(3, '0')
              let tempItemList = this.itemList.filter(item => item.riskItem !== riskItem)
              this.itemList = clone(tempItemList)
            }
            this.loading = false
            this.policy.loading = false

            this.$toast.success('Item Successfully Deleted.')
          } else {
            this.loading = false
            this.policy.loading = false
          }
        } catch (err) {
          this.loading = false
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doCloseItemTxt() {
      this.itemTextForm.itemUppTxt = this.resetItemTextForm.itemUppTxt
      this.itemTextForm.itemLwrTxt = this.resetItemTextForm.itemLwrTxt
      this.itemTextDialog = false
    },
    async doSaveItem() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.saveItem
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.loading = true
          this.getEmlAmt()
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            itemNo: this.mainForm.itemNo,
            delItem: this.mainForm.delItem,
            emlPct: this.mainForm.emlPct,
            emlAmt: removeFormatNumber(this.mainForm.emlAmt),
            stdItemTxtRef: this.mainForm.stdItemTxtRef,
            indGrp: this.mainForm.indGrp
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.loading = false
            this.policy.loading = false
          } else {
            this.loading = false
            this.policy.loading = false
          }
        } catch (err) {
          this.loading = false
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doSaveItemTxt() {
      this.itemTextForm.polNo = this.polMaster.polNo
      this.itemTextForm.renCnt = this.polMaster.renCnt
      this.itemTextForm.endtCnt = this.polMaster.endtCnt
      this.itemTextForm.riskGrpNo = this.mainForm.riskGrpNo
      this.itemTextForm.riskNo = this.mainForm.riskNo
      this.itemTextForm.itemNo = this.mainForm.itemNo
      this.itemTextForm.indGrp = this.mainForm.indGrp

      this.policy.loading = true
      this.doItemTxtSave().then(success => {
        if (success) {
          this.resetItemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt
          this.resetItemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt
          this.$toast.success('Item Text Successfully Saved')
          this.riskItemData()
        } else {
          this.$toast.error('Item Text Save Failed')
        }
        this.policy.loading = false
      })
    },
    async doItemTxtSave() {
      try {
        if (this.itemTextForm.itemUppTxt === undefined) {
          this.itemTextForm.itemUppTxt = ''
        }
        if (this.itemTextForm.itemLwrTxt === undefined) {
          this.itemTextForm.itemLwrTxt = ''
        }
        if (this.itemTextForm.indGrp === undefined) {
          this.itemTextForm.indGrp = ''
        }
        const result = await this.$store.dispatch('savePolItem', this.itemTextForm)
        return result
      } catch (err) {
        return false
      }
    },
    doSavePolItemEml() {
      try {
        let expand = {
          polNo: this.polMaster.polNo,
          renCnt: this.polMaster.renCnt,
          endtCnt: this.polMaster.endtCnt,
          riskGrpNo: this.coverageSelectedRiskGrpNo,
          riskNo: this.coverageSelectedRiskNo,
          itemNo: this.coverageSelectedItemNo,
          emlAmt: this.mainForm.emlAmt,
          emlPct: this.mainForm.emlPct
        }
        const result = this.$store.dispatch('savePolItemEml', expand)
        return result
      } catch (err) {
        return false
      }
    },
    doStdTxtRef() {
      try {
        let tempMainForm = {
          stdItemTxtRef: this.mainForm.stdItemTxtRef,
          stdRiskTxtRef: this.mainForm.stdRiskTxtRefId,
          indGrp: this.mainForm.indGrp
        }

        let tempCloneMainForm = {
          stdItemTxtRef: this.cloneMainForm.stdItemTxtRef,
          stdRiskTxtRef: this.cloneMainForm.stdRiskTxtRefId,
          indGrp: this.cloneMainForm.indGrp
        }

        if (isEqual(tempMainForm, tempCloneMainForm)) {
          return 'NOCHANGE'
        }

        let expand = {
          polNo: this.polMaster.polNo,
          renCnt: this.polMaster.renCnt,
          endtCnt: this.polMaster.endtCnt,
          riskGrpNo: this.coverageSelectedRiskGrpNo,
          riskNo: this.coverageSelectedRiskNo,
          itemNo: this.coverageSelectedItemNo,
          stdItemTxtRef: this.mainForm.stdItemTxtRef,
          stdRiskTxtRef: this.mainForm.stdRiskTxtRefId,
          indGrp: this.mainForm.indGrp
        }
        this.$store.dispatch('savePolItemTxtRef', expand).then(result => {
          if (result) {
            this.resetItemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt
            this.resetItemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt

            let index = this.itemList.findIndex(item => item.riskItem === this.mainForm.riskItem)
            if (index >= 0) {
              let item = this.itemList[index]
              item.stdItemTxtRef = this.mainForm.stdItemTxtRef
              item.stdItemTxtLn = this.mainForm.stdItemTxtLn
              item.indGrp = this.mainForm.indGrp
              this.itemList[index] = item

              this.cloneMainForm.stdItemTxtRef = this.mainForm.stdItemTxtRef
              this.cloneMainForm.stdItemTxtLn = this.mainForm.stdItemTxtLn
              this.cloneMainForm.indGrp = this.mainForm.indGrp
            }
            return 'SUCCESS'
          } else {
            this.$toast.error('Item Text Save Failed')
            return 'FAIL'
          }
        })
      } catch (err) {
        return 'FAIL'
      }
    },
    doLoadPreset() {
      this.defTextSelected = []
      if (this.tabMenu === 0) {
        this.scnRefNo = '30'
        this.defTextSelected = this.selectedItemUppTxt
        this.defaulText = this.itemsItemUppTxt
      } else if (this.tabMenu === 1) {
        this.scnRefNo = '31'
        this.defTextSelected = this.selectedItemLwrTxt
        this.defaulText = this.itemsItemLwrTxt
      }
    },
    doSelectDefText() {
      if (this.tabMenu === 0) {
        if (this.itemTextForm.itemUppTxt === undefined) {
          this.itemTextForm.itemUppTxt = ''
        }
        if (this.itemTextForm.itemUppTxt.length > 0 && this.itemTextForm.itemUppTxt.charAt(this.itemTextForm.itemUppTxt.length - 1) !== '\n') {
          this.itemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt + '\n'
        }
        for (var a = 0; a < this.defTextSelected.length; a++) {
          this.itemTextForm.itemUppTxt = this.itemTextForm.itemUppTxt + this.defTextSelected[a].defTxt + '\n'
        }
      }
      if (this.tabMenu === 1) {
        if (this.itemTextForm.itemLwrTxt === undefined) {
          this.itemTextForm.itemLwrTxt = ''
        }
        if (this.itemTextForm.itemLwrTxt.length > 0 && this.itemTextForm.itemLwrTxt.charAt(this.itemTextForm.itemLwrTxt.length - 1) !== '\n') {
          this.itemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt + '\n'
        }
        for (var b = 0; b < this.defTextSelected.length; b++) {
          this.itemTextForm.itemLwrTxt = this.itemTextForm.itemLwrTxt + this.defTextSelected[b].defTxt + '\n'
        }
      }
      this.loadPreSetDialog = false
    },
    async getItemTextList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.itemTextList
      if (apiUrl) {
        try {
          const expand = {
            busCls: this.mainForm.busCls
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data.itemUppTxtList) {
            const { data } = result.data.itemUppTxtList
            if (data) {
              this.itemsItemUppTxt = []
              this.selectedItemUppTxt = []
              data.map(upperText => {
                const upperTextList = {
                  txtRef: upperText.txtRef,
                  txtTitle: upperText.txtTitle,
                  defTxt: upperText.defTxt
                }
                this.itemsItemUppTxt.push(upperTextList)
                if (upperText.txtDef === true) {
                  this.selectedItemUppTxt.push(upperTextList)
                }
                for (let i in this.selectedItemUppTxt) {
                  this.itemTextForm.itemUppTxt = this.selectedItemUppTxt[i].txtTitle + '\n'
                }
              })
            } else {
              this.itemsItemUppTxt = []
            }
          }

          if (result.data.itemLwrTxtList) {
            const { data } = result.data.itemLwrTxtList
            if (data) {
              this.itemsItemLwrTxt = []
              this.selectedItemLwrTxt = []
              data.map(lowerText => {
                const lowerTextList = {
                  txtRef: lowerText.txtRef,
                  txtTitle: lowerText.txtTitle,
                  defTxt: lowerText.defTxt
                }
                this.itemsItemLwrTxt.push(lowerTextList)
                if (lowerText.txtDef === true) {
                  this.selectedItemLwrTxt.push(lowerTextList)
                }
                for (let i in this.itemsItemLwrTxt) {
                  this.itemTextForm.itemLwrTxt = this.itemsItemLwrTxt[i].txtTitle + '\n'
                }
              })
            } else {
              this.itemsItemLwrTxt = []
            }
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doItemStatsSave() {
      this.polItemStatsForm.polNo = this.polMaster.polNo
      this.polItemStatsForm.renCnt = this.polMaster.renCnt
      this.polItemStatsForm.endtCnt = this.polMaster.endtCnt
      this.polItemStatsForm.riskGrpNo = this.mainForm.riskGrpNo
      this.polItemStatsForm.riskNo = this.mainForm.riskNo
      this.polItemStatsForm.itemNo = this.mainForm.itemNo

      this.polItemStatsForm.statsType = []
      this.polItemStatsForm.statsVal = []
      for (var i = 0; i < this.polItemStats.items.length; i++) {
        this.polItemStatsForm.statsType.push(this.polItemStats.items[i].statsType)
        this.polItemStatsForm.statsVal.push(this.polItemStats.items[i].statsVal)
      }
      try {
        this.polItemStats.saving = true
        const saveResult = await this.$store.dispatch('savePolItemStats', this.polItemStatsForm)
        if (saveResult) {
          this.$toast.success('Policy Item Statistics has been Saved')
          this.polItemStats.saving = false
        } else {
          this.$toast.error('Policy Item Statistics Save Failed')
          this.polItemStats.saving = false
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    doShowStatValue(data) {
      this.getStatsValue(data.statsType)
      this.statsValDialog = true
    },
    async getStatsValue(statsType) {
      this.statsVal.items = []
      const apiUrl = this.$store.state.underwriting.policyHeaderSettings.menu.statsValList
      if (apiUrl) {
        try {
          this.statsVal.loading = true
          const expand = {
            statsType: statsType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(statsValue => {
              const statsValueList = {
                statsVal: statsValue.statsVal,
                statsValDesc: statsValue.statsValDesc,
                statsType: statsType
              }
              this.statsVal.items.push(statsValueList)
            })
            this.statsVal.loading = false
          } else {
            this.statsVal.items = []
            this.statsVal.loading = false
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doSelectedStatsValue(data) {
      for (var i = 0; i < this.polItemStats.items.length; i++) {
        if (this.polItemStats.items[i].statsType === data.statsType) {
          this.polItemStats.items[i].statsVal = data.statsVal
          this.polItemStats.items[i].statsValDesc = data.statsValDesc
        }
      }
      this.statsValDialog = false
    },
    doExit() {
      this.$router.push({ name: 'policyListing' })
    },
    doReset() {
      this.$refs.polDiscLdg.doReset()
    },
    async doSave() {
      this.resetMessages()
      let result = await this.$validator.validateAll()
      let output = 'ERROR'
      if (result) {
        this.policy.saving = true
        let successVal = await this.valAllItemStatsVal()
        await this.doSaveItem()
        if (successVal) {
          await this.doStdTxtRef()
          if (this.$refs.polItemUOM.mainForm.seqNo !== undefined) {
            output = await this.$refs.polItemUOM.doSaveUOM()
          } else if (this.$refs.polItemBen.mainForm.seqNo !== undefined) {
            output = await this.$refs.polItemBen.doSave()
          } else if (this.$refs.polDiscLdg) {
            output = await this.$refs.polDiscLdg.doSave()
          } else {
            output = 'SUCCESS'
            this.$toast.success(this.successMessage())
          }
          this.policy.saving = false
        } else {
          this.policy.saving = false
        }
      } else {
        this.policy.saving = false
        this.promptFieldCheck()
      }
      return output
    },
    async doProceed() {
      this.doSave().then(output => {
        if (output === 'SUCCESS' || output === 'NOCHANGE') {
          this.$store.commit(POLICY_RISK_TAB, 3)
          this.$router.push({ name: 'policyRiskTrailer' })
        }
      })
    },
    getRiskItemBenList() {
      this.$refs.polItemBen.getPolItemBenList()
    },
    successMessage() {
      return 'Policy record successfully saved.'
    },
    failedMessage() {
      return 'An unexpected error occured while saving policy record.'
    },
    async refreshItemDetails() {
      // this.getRiskItemBenList()
      this.riskItemDataAndCalculateAndRiskItemList()
    },
    // async refreshItemDetails(uomForm) {
    //   if (uomForm.seqNo === 1) {
    //     this.mainForm.emlAmt = formatPrice(this.calculateEmlAmt(uomForm.uomVal))
    //     this.doSavePolItemEml()
    //     this.riskItemDataAndCalculateAndRiskItemList()
    //   }
    // },
    doSavePolicyDiscLdg() {
      this.riskItemData()
      if (this.$refs.polItemBen) {
        this.$refs.polItemBen.getPolItemBenList()
      }
      this.calcAndUpdateSpecificPRAndPM()
    },
    async getEmlAmt() {
      // Calculated as: [polItemUOM.uomVal] * [polItemUOM.emlPct] / 100
      if (this.$refs.polItemUOM.getFirstUOM.length > 0) {
        this.mainForm.emlAmt = formatPrice(this.calculateEmlAmt(this.$refs.polItemUOM.getFirstUOM[0].uomVal))
      } else {
        this.mainForm.emlAmt = formatPrice(0)
      }
    },
    calculateEmlAmt(uomVal) {
      return (removeFormatNumber(uomVal) * removeFormatNumber(this.mainForm.emlPct)) / 100
    },
    doClearStdItemTxtRef() {
      this.mainForm.stdItemTxtLn = ''
    },
    // 2020-12-17 LYT SR5681
    async getRiskList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getPRRiskGrpNoAndRiskNoList
      if (apiUrl) {
        try {
          this.polRiskLoading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            data.data.map(polRisk => {
              const tmpRiskNos = {
                label: 'Risk ' + polRisk.value.toString().padStart(2, '0') + '/' + polRisk.secondaryValue.toString().padStart(3, '0'),
                value: polRisk.value.toString().padStart(2, '0') + '/' + polRisk.secondaryValue.toString().padStart(3, '0')
              }
              this.riskNos.push(tmpRiskNos)
            })
            this.polRiskLoading = false
          } else {
            this.polRiskLoading = false
          }
        } catch (err) {
          this.polRiskLoading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doAssignIndustryHelpList(industry) {
      this.mainForm.indGrp = industry.indGrp
      this.indGrpDesc = industry.indGrpDesc
      // this.getIndGrp()
    },
    doIndGrpDesc() {
      if (this.getIndGrpDesc.length > 0) {
        this.indGrpDesc = this.getIndGrpDesc[0].label
      } else {
        this.indGrpDesc = ''
      }
    },
    async doPolBordItemList() {
      if (this.checkItemListByRiskNo > 0 || this.checkItemListByItemNo > 0) {
        const errMSg = {
          messages: [{ message: 'Not permitted for a policy with multiple risks / items. (10316)', type: 'ERROR' }]
        }
        this.message(errMSg)
      } else {
        this.showPolBordItemList = true
        if (this.$refs.polBordItem) this.$refs.polBordItem.doInit()
      }
    },
    async valAllItemStatsVal() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.valAllItemStatsVal
      if (apiUrl) {
        try {
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          await this.$http.get(expandURI(apiUrl, expand))
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url. [valAllItemStatsVal]')
      }
    },
    processShowTxn(option) {
      if (option === 'IUOM') {
        this.progressTxnIUOM = false
        this.progressTxnPIB = true
        this.progressTxnDL = true
      } else if (option === 'PIB') {
        this.progressTxnIUOM = true
        this.progressTxnPIB = false
        this.progressTxnDL = true
      } else if (option === 'DL') {
        this.progressTxnIUOM = true
        this.progressTxnPIB = true
        this.progressTxnDL = false
      } else {
        this.progressTxnIUOM = false
        this.progressTxnPIB = false
        this.progressTxnDL = false
      }
    },
    showExpandRiskItemReset() {
      this.showExpandRiskItem = false
      this.processShowTxn('RESET')
    },
    async reCalcCommDisc() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.reCalcCommDisc
      if (this.saveWhenLeave === false) {
        if (apiUrl) {
          this.policy.saving = true
          this.saveWhenLeave = false
          try {
            this.premDueLoading = true

            if (this.mainForm.gap === '') {
              this.mainForm.gap = 0
            }
            const expand = {
              polNo: this.polMaster.polNo,
              renCnt: this.polMaster.renCnt,
              endtCnt: this.polMaster.endtCnt
            }
            const result = await this.$http.get(expandURI(apiUrl, expand))
            if (result) {
              this.saveWhenLeave = true
              this.policy.saving = false
            }
            return true
          } catch (err) {

          }
        } else {
          this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        }
      }
    }
  },
  components: {
    PolicyItemUOM,
    PolicyDiscLdg,
    PolicyItemBen,
    StdRiskTxtHelpList,
    StdItemTxtHelpList,
    PerIndGrpHelpList,
    PolicyBordItemList
  },
  async beforeRouteLeave(to, from, next) {
    await this.reCalcCommDisc()
    if (this.saveWhenLeave === true) {
      next()
    }
  },
  beforeMount() {
    if (this.$store.state.underwriting.policyRiskItemSettings.init !== 1) {
      this.$store.dispatch('policyRiskItemInit').then(() => {
        this.doInit()
      })
    } else {
      this.doInit()
    }
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>
